import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { initVirtualPos, makePosPayment } from '../../../api/endpoints/pos';
import { getPayableBanks } from '../../../api/endpoints/payment';
import type { VirtualPosShoppingCart } from '../../../api/types/pos';
import type { PayableBankInfo } from '../../../api/types/payment';
import type { Currency } from '../../../api/types/common';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';

const foldTr = (s: string) =>
  s
    .toLocaleLowerCase('tr-TR')
    .replace(/[çÇ]/g, 'c')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[ıİ]/g, 'i')
    .replace(/[öÖ]/g, 'o')
    .replace(/[şŞ]/g, 's')
    .replace(/[üÜ]/g, 'u')
    .replace(/[^a-z0-9]/g, '');

/**
 * Resolve a POS installment option's `bankName` (e.g. "Garanti", "Yapı Kredi") to the
 * backend `PayableBank` ordinal, matching against `Payment/GetPayableBanks` — no more
 * hardcoded map (see api/types/pos.ts header, docs/backend-requirements.md F-8).
 */
function resolvePayableBank(bankName: string | undefined, banks: PayableBankInfo[]): number | undefined {
  if (!bankName) return undefined;
  const target = foldTr(bankName);
  const hit = banks.find((b) => {
    const n = foldTr(b.name ?? '');
    const d = foldTr(b.displayName ?? '');
    return n === target || d === target || (target.length > 2 && (n.includes(target) || target.includes(n) || d.includes(target) || target.includes(d)));
  });
  return hit?.value;
}

const amountSchema = z.object({
  amount: z.coerce.number().positive('Geçerli bir tutar girin'),
});
type AmountValues = z.infer<typeof amountSchema>;

const cardSchema = z.object({
  installmentOptionId: z.string().min(1, 'Banka / taksit seçimi gerekli'),
  cardHolder: z.string().min(1, 'Kart üzerindeki isim gerekli'),
  cardNumber: z
    .string()
    .min(15, 'Geçerli bir kart numarası girin')
    .max(19, 'Geçerli bir kart numarası girin')
    .regex(/^\d+$/, 'Sadece rakam girin'),
  cvv: z.string().min(3, 'Geçerli bir CVV girin').max(4, 'Geçerli bir CVV girin'),
  expirationMonth: z
    .string()
    .length(2, 'Ay 2 haneli olmalı (ör. 09)')
    .regex(/^(0[1-9]|1[0-2])$/, 'Geçerli bir ay girin'),
  expirationYear: z.string().length(2, 'Yıl 2 haneli olmalı (ör. 30)').regex(/^\d{2}$/, 'Geçerli bir yıl girin'),
});
type CardValues = z.infer<typeof cardSchema>;

/**
 * Pos Çekim / Kredi Yükle — migration guide Bölüm 3.7/3.10-adjacent + 5.3 "Pos".
 * Two-step flow confirmed via curl against the real API:
 *   1. InitVirtualPos({amount}) → creates a ShoppingCart with installment options
 *      per bank (no card fields at this step — the guide's guess was wrong).
 *   2. MakePayment({shoppingCartId, paymentForm: {...card fields}}) against that
 *      cart, same shared endpoint every Phase 1 module uses for checkout.
 */
export default function PosTransactionPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [cart, setCart] = useState<VirtualPosShoppingCart | null>(null);

  // Acquiring-bank list — best-effort; if it fails the payment still goes through
  // (backend resolves the bank from the card BIN when payableBank is omitted).
  const { data: payableBanksRes } = useQuery({
    queryKey: ['payableBanks'],
    queryFn: getPayableBanks,
    staleTime: 5 * 60 * 1000,
  });
  const payableBanks = useMemo(() => payableBanksRes?.banks ?? [], [payableBanksRes]);

  const {
    register: registerAmount,
    handleSubmit: handleAmountSubmit,
    formState: { errors: amountErrors },
  } = useForm<AmountValues>({ resolver: zodResolver(amountSchema), defaultValues: { amount: 0 } });

  const initMutation = useMutation({
    mutationFn: (values: AmountValues) => initVirtualPos({ amount: values.amount }),
    // apiPost already throws ApiError for hasError:true responses (see api/client.ts),
    // so a resolved mutation here is always a clean success.
    onSuccess: (res) => setCart(res.shoppingCart ?? null),
  });

  const {
    register: registerCard,
    handleSubmit: handleCardSubmit,
    formState: { errors: cardErrors },
    reset: resetCardForm,
  } = useForm<CardValues>({
    resolver: zodResolver(cardSchema),
    defaultValues: {
      installmentOptionId: '',
      cardHolder: '',
      cardNumber: '',
      cvv: '',
      expirationMonth: '',
      expirationYear: '',
    },
  });

  const installmentOptions = cart?.paymentOption?.installmentOptions ?? [];

  const payMutation = useMutation({
    mutationFn: (values: CardValues) => {
      const option = installmentOptions.find((o) => o.installmentOptionId === values.installmentOptionId);
      return makePosPayment({
        shoppingCartId: cart?.id ?? '',
        amount: option?.remainingSum ?? option?.subTotal ?? 0,
        paymentType: 'CreditCardPayment',
        // Standalone virtual-POS screen — affects the 3-D completion path server-side.
        paymentChannel: 1,
        payableBank: resolvePayableBank(option?.bankName, payableBanks),
        paymentForm: {
          cardHolder: values.cardHolder,
          cardNumber: values.cardNumber,
          cvv: values.cvv,
          expirationMonth: values.expirationMonth,
          expirationYear: values.expirationYear,
          installmentId: values.installmentOptionId,
          installmentCount: option?.installmentCount ?? 1,
          currency: (option?.currency as Currency | undefined) ?? 'TRY',
          returnUrl: `${window.location.origin}${window.location.pathname}`,
        },
      });
    },
    // apiPost already throws ApiError for hasError:true responses.
    onSuccess: () => {
      enqueueSnackbar('Pos işlemi gönderildi.', { variant: 'success' });
      resetCardForm();
      setCart(null);
    },
  });

  const submitAmount = handleAmountSubmit((values) => initMutation.mutate(values));
  const submitCard = handleCardSubmit((values) => payMutation.mutate(values));

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Pos Çekim / Kredi Yükle
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
            <CreditCardIcon color="action" />
            <Typography variant="h6">Tutar</Typography>
          </Stack>
          <Box component="form" onSubmit={submitAmount}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
              <TextField
                size="small"
                label="Tutar (TRY)"
                type="number"
                error={!!amountErrors.amount}
                helperText={amountErrors.amount?.message}
                {...registerAmount('amount')}
              />
              <Button type="submit" variant="contained" disabled={initMutation.isPending}>
                {initMutation.isPending ? 'Hazırlanıyor…' : 'Devam Et'}
              </Button>
            </Stack>
            <ApiErrorAlert error={initMutation.error} sx={{ mt: 2 }} />
          </Box>
        </CardContent>
      </Card>

      {cart && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Kart Bilgileri
            </Typography>
            {installmentOptions.length === 0 ? (
              <Alert severity="warning">Bu tutar için uygun banka/taksit seçeneği bulunamadı.</Alert>
            ) : (
              <Box component="form" onSubmit={submitCard}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Banka / Taksit"
                      error={!!cardErrors.installmentOptionId}
                      helperText={cardErrors.installmentOptionId?.message}
                      defaultValue=""
                      {...registerCard('installmentOptionId')}
                    >
                      <MenuItem value="" disabled>
                        Seçiniz
                      </MenuItem>
                      {installmentOptions.map((o) => (
                        <MenuItem key={o.installmentOptionId} value={o.installmentOptionId}>
                          {o.bankName} — {o.installmentCount} Taksit — {o.montlyPayment} {o.currency}/ay
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Kart Üzerindeki İsim"
                      error={!!cardErrors.cardHolder}
                      helperText={cardErrors.cardHolder?.message}
                      {...registerCard('cardHolder')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Kart Numarası"
                      error={!!cardErrors.cardNumber}
                      helperText={cardErrors.cardNumber?.message}
                      {...registerCard('cardNumber')}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Ay (AA)"
                      placeholder="09"
                      error={!!cardErrors.expirationMonth}
                      helperText={cardErrors.expirationMonth?.message}
                      {...registerCard('expirationMonth')}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Yıl (YY)"
                      placeholder="30"
                      error={!!cardErrors.expirationYear}
                      helperText={cardErrors.expirationYear?.message}
                      {...registerCard('expirationYear')}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="CVV"
                      error={!!cardErrors.cvv}
                      helperText={cardErrors.cvv?.message}
                      {...registerCard('cvv')}
                    />
                  </Grid>
                </Grid>
                <ApiErrorAlert error={payMutation.error} sx={{ mt: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <Button type="submit" variant="contained" disabled={payMutation.isPending}>
                    {payMutation.isPending ? 'İşleniyor…' : 'Ödemeyi Tamamla'}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
